import info from '@shared/api/graphQL/epg/info';
import parser from '@shared/api/graphQL/parser';
import query from '@shared/api/graphQL/epg/query';
import Channel from '@shared/classes/channel';
import Direction from '@shared/types/enums/direction';
import IDate from '@shared/types/interfaces/date';
import Main from '@shared/types/enums/main';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { EventMap, IEPGRequest } from '@shared/types/interfaces/event';
import { PaginationParams } from '@shared/types/interfaces/query';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { eventsSelector } from '@shared/store/slices/events.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Constants
 */
const CHUNK_SIZE = 5;
const EPG_PAGE_SIZE = 10;
const ERROR_TIMEOUT = 5 * 1000;
const FETCH_DELAY = 0.5 * 1000;

/**
 * Defines the properties of a channel chunk.
 *
 * @property {string} index -The unique index of the chunk.
 * @property {number} data - The list of channel IDs associated with the chunk.
 */
interface Chunk {
  index: number;
  data: string[];
}

/**
 * Defines the properties useEpg hook can receive.
 *
 * @property {Channel[]} [channelsData] - The channel data array to retrieve chunks from.
 * @property {string | undefined} [channelId] - The ID of the channel for which EPG data is fetched.
 * @property {number | undefined} [chunkSize] - The number of channels to fetch per request.
 * @property {number | undefined} [pageSize] - The number of elements per channel to fetch.
 * @property {object | undefined} [offset] - Offset fetching configuration.
 * @property {number} offset.horizontal - Horizontal offset configuration.
 * @property {object} offset.vertical - Vertical offset configuration.
 * @property {boolean | undefined} offset.vertical.previous - Indicates the range of assets at the beginning of a chunk where fetching is required.
 * @property {boolean | undefined} offset.vertical.previous - Indicates the range of assets at the end of a chunk where fetching is required.
 */
interface Props {
  channelsData?: Channel[];
  channelId?: string;
  chunkSize?: number;
  dateRange?: IDate;
  pageSize?: number;
  offset?: {
    horizontal?: number;
    vertical?: {
      next?: number;
      previous?: number;
    };
  };
  previousEpg?: EventMap;
  ready?: boolean;
  useIncoming?: boolean;
}

/**
 * Epg hook. Fetches and returns epg data.
 */
export default function useEpg({
  channelsData,
  channelId,
  chunkSize,
  dateRange,
  pageSize,
  offset,
  previousEpg,
  ready = true,
  useIncoming = true,
}: Props) {
  const { favouriteChannels } = useAppSelector(channelsSelector);
  const { eventsData } = useAppSelector(eventsSelector);

  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(!previousEpg);
  const [epg, setEpg] = useState<EventMap>(
    previousEpg || (useIncoming ? eventsData : {})
  );
  const [variables, setVariables] = useState<object | undefined>(undefined);

  const channels = useRef(channelsData || favouriteChannels || []);
  const client = useApolloClient();
  const index = useRef(0);
  const paginating = useRef(false);
  const chunks = useRef<
    Record<
      number,
      {
        data: string[];
        pagination: {
          params: PaginationParams;
          maxItems: number;
          maxPages: number;
        };
      }
    >
  >({});

  channels.current = channelsData || favouriteChannels || [];

  // --------------------------
  // Event handlers

  /**
   * Request completion callback.
   */
  const onCompleted = useCallback(() => {
    // Complete request
    setComplete(true);
    setLoading(false);
  }, []);

  /**
   * Request error callback.
   */
  const onError = useCallback((error: ApolloError) => {
    // Print error
    console.error(error.message);
    // Complete request
    setComplete(true);
    setLoading(false);
  }, []);

  /**
   * Handles horizontal pagination for the specified content type.
   * Updates query variables to trigger data fetching in the desired direction.
   *
   * @param {Main} type - The specific content type to paginate.
   * @param {Direction} direction - The pagination direction.
   */
  const onPagination = useCallback(
    (_type: Main | null, direction: Direction) => {
      /**
       * Horizontal reel displacement
       */
      if (!paginating.current && direction === Direction.HORIZONTAL) {
        const currentPage =
          chunks.current[index.current].pagination.params.page;
        const nextPage = currentPage + 1;
        if (nextPage <= chunks.current[index.current].pagination.maxPages) {
          // Set query variables so that new data is fetched
          chunks.current[index.current].pagination.params.page = nextPage;
        }
        // Halt callback
        else {
          return;
        }
      } else {
        return;
      }

      /**
       * Fetches the next set of results for a paginated list field
       */
      fetchContents({
        chunk: {
          data: chunks.current[index.current].data,
          index: index.current,
        },
      });
    },
    [complete]
  );

  // --------------------------
  // Logic related functions

  /**
   * Retrieves a fixed chunk of channel identifers based on the current channelId.
   *
   * Chunks are grouped in fixed sizes, meaning any channelId within the same chunk
   * will always return the same set of values unless an offset is provided.
   *
   * @param {string} channelId - The channel identifier.
   * @param {number} chunkOffset - The offset of the chunk to retrieve relative to the current one (default: 0).
   * @param {number} chunkSize - The fixed chunk size (default: 5).
   * @returns {Chunk} The index of the chunk and the channels' identifiers or an empty array if not found.
   */
  const getChunk = useCallback(
    ({
      channelId,
      chunkOffset = 0,
      chunkSize = CHUNK_SIZE,
    }: {
      channelId?: string;
      chunkOffset?: number;
      chunkSize?: number;
    }): Chunk | null => {
      if (!channelId) return null;
      if (!Array.isArray(channels.current)) return null;

      // Fetch current channel index
      const index = channels.current?.findIndex(
        (channel) => channel.id === channelId
      );
      if (index === -1) return null;

      // Determine total chunks in the list
      const totalChunks = Math.ceil(channels.current.length / chunkSize);

      // Calculate current chunk index
      const currentChunkIndex = Math.floor(index / chunkSize);

      // Compute new chunk index with wrap-around
      const newChunkIndex =
        (currentChunkIndex + chunkOffset + totalChunks) % totalChunks;
      const chunkStart = newChunkIndex * chunkSize;

      // Return sliced chunk mapped to channels' identifiers
      return {
        index: newChunkIndex,
        data:
          channels.current
            .slice(chunkStart, chunkStart + chunkSize)
            .map((channel) => channel.id) || [],
      };
    },
    []
  );

  /**
   * Retrieves query required variables.
   *
   * @param {string} chunk - The chunk to retrieve variables for.
   */
  const getVariables = useCallback(
    (chunk: Chunk) => {
      const { data, index: newIndex } = chunk;
      if (!chunks.current[newIndex]) {
        chunks.current[newIndex] = {
          data,
          pagination: {
            params: {
              page: 1,
              pageSize: pageSize || EPG_PAGE_SIZE,
            },
            maxItems: 0,
            maxPages: 1,
          },
        };
      }
      return {
        epgHorizontalParams: dateRange
          ? {}
          : chunks.current[index.current]?.pagination.params,
        ...info(chunk.data, dateRange),
      };
    },
    [dateRange]
  );

  /**
   * Requests a new chunk based on the current channel's chunk and a given offset
   *
   * @param {string} channelId - The channel to fetch data from.
   * @param {Chunk} chunk - The chunk to fetch data from. If null, it is retrieved from the current channel ID.
   * @param {number} chunkOffset - The offset of the chunk to retrieve relative to the current one (default: 0).
   */
  const fetchContents = useCallback(
    ({
      channelId,
      chunk,
      chunkOffset = 0,
      forceFetch = false,
    }: {
      channelId?: string;
      chunk?: Chunk | null;
      chunkOffset?: number;
      forceFetch?: boolean;
    }) => {
      chunk = chunk || getChunk({ channelId, chunkOffset, chunkSize });
      if (chunk) {
        const variables = getVariables(chunk);
        const params = {
          query,
          variables,
        };
        // Force fetch if required
        const data: IEPGRequest | null = client.readQuery(params);
        if (forceFetch && !data) {
          fetchMore(params).then((value) => {
            if (value.data) {
              updateContents(value.data);
            } else {
              setLoading(false);
            }
          });
          return;
        } else if (forceFetch && data) {
          updateContents(data);
          return;
        }
        // Calculate page to fetch
        const fetchedPage =
          data?.epgs?.items?.reduce((prev, item) => {
            const page = Math.max(
              Math.ceil((item.epgs?.length || 0) / (pageSize || EPG_PAGE_SIZE)),
              1
            );
            if (page > prev) {
              return page;
            }
            return prev;
          }, 0) || 0;
        // Fetch new page if possible
        if (fetchedPage < chunks.current[chunk.index].pagination.maxPages) {
          // Update page to fetch if current is fetched already
          if (
            fetchedPage >= chunks.current[chunk.index].pagination.params.page
          ) {
            // Set current page to next page if already fetched
            if (
              fetchedPage > chunks.current[chunk.index].pagination.params.page
            ) {
              chunks.current[chunk.index].pagination.params.page =
                fetchedPage + 1;
            }
            // If page to fetch is equal to fetchedPage, return
            else {
              return;
            }
            // Set new params
            if (params.variables?.epgHorizontalParams) {
              params.variables.epgHorizontalParams.page =
                chunks.current[chunk.index].pagination.params.page;
            }
          }
          paginating.current = true;
          fetchMore(params).then((value) => {
            if (value.data) {
              updateContents(value.data);
            } else {
              setLoading(false);
            }
          });
        }
        // If data was already fetched, set current page
        else {
          chunks.current[chunk.index].pagination.params.page = fetchedPage;
        }
      }
    },
    [channelId, dateRange]
  );

  /**
   * Parses a given response and updates main contents
   *
   * @param {IEPGRequest} response - Retrieved EPG data to be parsed.
   */
  const updateContents = useCallback(
    (response: IEPGRequest) => {
      try {
        // Clear paginating flag if required
        if (paginating.current) {
          paginating.current = false;
        }
        // Update epg and pagination data
        const result = parser.epgs(response, epg, !dateRange);
        if (result) {
          chunks.current[index.current].pagination.maxItems = result.maxItems;
          chunks.current[index.current].pagination.maxPages = result.maxPages;
          setEpg(result.map);
          setLoading(false);
        }
      } catch (e) {
        // Print error
        console.error(e);
      }
    },
    [dateRange, epg]
  );

  // --------------------------
  // Query hook

  /**
   * Fetch epg data via graphql using the query hook
   */
  const { data, fetchMore } = useQuery(query, {
    skip: !variables || !ready,
    variables,
    onCompleted,
    onError,
  });

  // --------------------------
  // Effect hooks

  /**
   * Fetch channel's associated chunk and preload surrounding chunks when required
   */
  useEffect(() => {
    if (!channelId) return;
    const timeout = setTimeout(
      () => {
        const chunk = getChunk({ channelId, chunkSize });
        if (chunk) {
          // Load current chunk
          const { data, index: newIndex } = chunk;
          index.current = newIndex;
          setVariables(getVariables(chunk));
          // Check if next chunk should be loaded
          if (
            complete &&
            ready &&
            offset?.vertical?.next &&
            data.indexOf(channelId) > data.length - offset.vertical?.next - 1
          ) {
            fetchContents({ channelId, chunkOffset: 1 });
          }
          // Check if previous chunk should be loaded
          if (
            complete &&
            ready &&
            offset?.vertical?.previous &&
            data.indexOf(channelId) < offset?.vertical?.previous
          ) {
            fetchContents({ channelId, chunkOffset: -1 });
          }
        }
      },
      ready ? FETCH_DELAY : 0
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [channelId]);

  useEffect(() => {
    error && setError(false);
    let timeout: NodeJS.Timeout | null = null;
    if (!complete) return;
    if (channelId) {
      setLoading(true);
      fetchContents({ channelId, forceFetch: true });
      timeout = setTimeout(() => {
        setError(true);
        setLoading(false);
      }, ERROR_TIMEOUT);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [dateRange]);

  /**
   * Updates data on change
   */
  useEffect(() => {
    data && updateContents(data);
  }, [data]);

  /**
   * Update chunks on filter change
   */
  useEffect(() => {
    chunks.current = {};
  }, [channelsData]);

  // --------------------------

  return {
    complete,
    loading,
    epg,
    error,
    onPagination,
  };
}
